import { clients } from 'lib/https';
import { getConfigurationSettings } from 'services/configuration-service';
import {
  CreateSkillPayload,
  DraftSkillResponse,
  GetSkillsPayload,
  GetSkillsResponse,
  ImportSkillPayload,
  ImportSkillResponse,
  OptimizeSkillFieldPayload,
  OptimizeSkillFieldResponse,
  SkillDetails,
  SkillResource,
  UpdateSkillPayload,
} from './skills.types';

const parseDownloadFilename = (response: Response, fallback: string): string => {
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  return match?.[1] || fallback;
};

class SkillsService {
  async getSkills(payload: GetSkillsPayload, blocksKey: string): Promise<GetSkillsResponse> {
    const config = await getConfigurationSettings();
    const params = new URLSearchParams({
      page: String(payload.page),
      page_size: String(payload.pageSize),
    });

    if (payload.search_query?.trim()) {
      params.set('search_query', payload.search_query.trim());
    }

    if (payload.surface?.trim()) {
      params.set('surface', payload.surface.trim());
    }

    if (payload.installed !== undefined) {
      params.set('installed', String(payload.installed));
    }

    if (payload.visibility) {
      params.set('visibility', payload.visibility);
    }

    if (payload.order) {
      params.set('order', payload.order);
    }

    if (payload.order_by) {
      params.set('order_by', payload.order_by);
    }

    if (payload.pack_id) {
      params.set('pack_id', payload.pack_id);
    }

    return clients.get(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.skills}?${params.toString()}`,
      blocksKey
    );
  }

  async getSkillById(skillId: string, blocksKey: string): Promise<SkillDetails> {
    const config = await getConfigurationSettings();
    return clients.get(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.skills}/${encodeURIComponent(skillId)}`,
      blocksKey
    );
  }

  async createSkill(payload: CreateSkillPayload, blocksKey: string): Promise<SkillDetails> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.skills}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  async installSkill(skillId: string, blocksKey: string): Promise<SkillDetails> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.skills}/${encodeURIComponent(skillId)}/install`,
      blocksKey,
      JSON.stringify({})
    );
  }

  async uninstallSkill(skillId: string, blocksKey: string): Promise<SkillDetails> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.skills}/${encodeURIComponent(skillId)}/uninstall`,
      blocksKey,
      JSON.stringify({})
    );
  }

  async updateSkill(skillId: string, payload: UpdateSkillPayload, blocksKey: string): Promise<SkillDetails> {
    const config = await getConfigurationSettings();
    return clients.put(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.skills}/${encodeURIComponent(skillId)}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  async deleteSkill(skillId: string, blocksKey: string): Promise<void> {
    const config = await getConfigurationSettings();
    return clients.delete(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.skills}/${encodeURIComponent(skillId)}`,
      blocksKey
    );
  }

  async importSkill(
    { file, visibility }: ImportSkillPayload,
    blocksKey: string
  ): Promise<ImportSkillResponse> {
    const config = await getConfigurationSettings();
    const formData = new FormData();
    formData.append('file', file);

    if (visibility) {
      formData.append('visibility', visibility);
    }

    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.skills}/import`,
      blocksKey,
      formData,
      {},
      true
    );
  }

  async duplicateSkill(skillId: string, blocksKey: string): Promise<SkillDetails> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.skills}/${encodeURIComponent(skillId)}/duplicate`,
      blocksKey,
      JSON.stringify({})
    );
  }

  async downloadSkill(skillId: string, blocksKey: string): Promise<{ blob: Blob; filename: string }> {
    const config = await getConfigurationSettings();
    const response = await clients.get<Response>(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.skills}/${encodeURIComponent(skillId)}/download`,
      blocksKey
    );
    const blob = await response.blob();
    const filename = parseDownloadFilename(response, `${skillId}.zip`);
    return { blob, filename };
  }

  async uploadSkillResource(skillId: string, file: File, blocksKey: string): Promise<SkillResource> {
    const config = await getConfigurationSettings();
    const formData = new FormData();
    formData.append('file', file);

    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.skills}/${encodeURIComponent(skillId)}/resources`,
      blocksKey,
      formData,
      {},
      true
    );
  }

  async deleteSkillResource(skillId: string, fileId: string, blocksKey: string): Promise<void> {
    const config = await getConfigurationSettings();
    await clients.delete(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.skills}/${encodeURIComponent(skillId)}/resources/${encodeURIComponent(fileId)}`,
      blocksKey
    );
  }

  async updateSkillResource(
    skillId: string,
    fileId: string,
    content: string,
    blocksKey: string
  ): Promise<SkillResource> {
    const config = await getConfigurationSettings();
    return clients.put(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.skills}/${encodeURIComponent(skillId)}/resources/${encodeURIComponent(fileId)}`,
      blocksKey,
      JSON.stringify({ content })
    );
  }

  async draftSkill(userPrompt: string, blocksKey: string): Promise<DraftSkillResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.skills}/draft`,
      blocksKey,
      JSON.stringify({ userPrompt })
    );
  }

  async optimizeSkillField(
    payload: OptimizeSkillFieldPayload,
    blocksKey: string
  ): Promise<OptimizeSkillFieldResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.skills}/optimize`,
      blocksKey,
      JSON.stringify(payload)
    );
  }
}

export const skillsService = new SkillsService();
